import { useState, useEffect, useCallback } from 'react';
import { coreApi } from '@shared/api/client';
import { Contact, CreateContactInput } from './types';

const INITIAL_FALLBACK_CONTACTS: Contact[] = [
  {
    id: 'c1',
    first_name: 'Sarah',
    last_name: 'Connor',
    email: 'sarah@acme.com',
    phone: '+1-555-0199',
    company_id: 'comp1',
    company_name: 'Acme Corporation',
    job_title: 'VP of Procurement',
    status: 'active',
    custom_fields: {
      linkedin_url: 'https://linkedin.com/in/sarahconnor',
      preferred_language: 'English',
    },
    created_at: new Date().toISOString(),
  },
  {
    id: 'c2',
    first_name: 'John',
    last_name: 'Doe',
    email: 'john@acme.com',
    phone: '+1-555-0144',
    company_id: 'comp1',
    company_name: 'Acme Corporation',
    job_title: 'CTO',
    status: 'active',
    custom_fields: {
      linkedin_url: 'https://linkedin.com/in/johndoe',
      preferred_language: 'Spanish',
    },
    created_at: new Date().toISOString(),
  },
];

export function useContacts() {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContacts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await coreApi.get<Contact[]>('/contacts');
      if (Array.isArray(data)) {
        setContacts(data);
      } else {
        setContacts(INITIAL_FALLBACK_CONTACTS);
      }
    } catch {
      setContacts(INITIAL_FALLBACK_CONTACTS);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const createContact = async (input: CreateContactInput): Promise<string> => {
    try {
      const created = await coreApi.post<Contact>('/contacts', input);
      const res = created || { ...input, id: `c-${Date.now()}` };
      setContacts((prev) => [res, ...prev]);
      return res.id;
    } catch {
      const local: Contact = {
        ...input,
        id: `c-${Date.now()}`,
        created_at: new Date().toISOString(),
      };
      setContacts((prev) => [local, ...prev]);
      return local.id;
    }
  };

  const updateContact = async (id: string, input: Partial<Contact>) => {
    try {
      const updated = await coreApi.put<Contact>(`/contacts/${id}`, input);
      setContacts((prev) => prev.map((c) => (c.id === id ? { ...c, ...updated, ...input } : c)));
    } catch {
      setContacts((prev) => prev.map((c) => (c.id === id ? { ...c, ...input } : c)));
    }
  };

  const deleteContact = async (id: string) => {
    try {
      await coreApi.delete(`/contacts/${id}`);
    } catch {
      // ignore
    }
    setContacts((prev) => prev.filter((c) => c.id !== id));
  };

  return {
    contacts,
    isLoading,
    error,
    createContact,
    updateContact,
    deleteContact,
    reload: fetchContacts,
  };
}
